import { produce } from 'immer';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getErrorMessage,
  setValidationErrors,
  showErrorToast,
} from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import { ErrorPanel } from '../../../../../../common/widgets/ErrorPanel';
import Input from '../../../../../../common/widgets/Input';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../../common/widgets/Notification';
import { DropdownOptionModel } from '../../../../../benefit/models/Common';
import { ErrorState, ErrorType } from '../../../../../benefit/models/Errors';
import { config } from '../../../../config';
import { AccountInfoContext } from '../../../../contexts/AccountInfoContext';
import { getOptionalLabel } from '../../../../helpers';
import { resources } from '../../../../resources/resources';
import { merchantAccountsService } from '../../../../services/merchant-accounts';
import { setAccountInvitationSentInfoMessage } from '../../Common/utils';

interface SendInvitationDialogProps {
  closeDialog: () => void;
}

interface AddUserForm {
  email?: string;
  role?: DropdownOptionModel;
  firstname?: string;
  lastname?: string;
  phoneNumber?: string;
}

export const SendInvitationDialog = (props: SendInvitationDialogProps) => {
  const { closeDialog } = props;
  const [form, setForm] = useState<AddUserForm>({});
  const [errors, setErrors] = useState<ErrorState>({});
  const [roles, setRoles] = useState<DropdownOptionModel[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const { t: translate } = useTranslation();
  const { accountInfo } = useContext(AccountInfoContext);

  const loadRoles = async (abortController?: AbortController) => {
    setIsLoading(true);
    const resp = await merchantAccountsService.getAccountRoles({
      signal: abortController?.signal,
    });
    if (abortController?.signal.aborted) return;
    setIsLoading(false);
    if (resp.hasErrors) {
      setShowErrorPanel(true);
    } else {
      setRoles(resp.data);
    }
  };

  useMount(loadRoles);

  const clearErrors = (field: string) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  const onSaveClick = async () => {
    setIsSaveLoading(true);
    const resp = await merchantAccountsService.sendInvitation({
      ...form,
      roleId: form.role?.value,
    });
    setIsSaveLoading(false);
    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      setAccountInvitationSentInfoMessage(
        resources.accounts.userInvited,
        {
          0: form.email,
        },
        accountInfo.manufacturerId,
      );
      closeDialog();
    }
  };

  if (isLoading)
    return (
      <Dialog closeDialog={closeDialog} showCloseIcon>
        <LoadingSpinner />
      </Dialog>
    );

  if (showErrorPanel)
    return (
      <Dialog closeDialog={closeDialog} showCloseIcon>
        <ErrorPanel />
      </Dialog>
    );

  return (
    <Dialog
      closeDialog={closeDialog}
      showCloseIcon
      className="dialog-merchants-edit"
    >
      <div className="dialog-content">
        <div className="dialog-title">
          {translate(resources.accounts.dialog.add.title)}
        </div>
        <div className="dialog-text centered mb-20">
          {translate(resources.accounts.dialog.add.info, {
            0: config.usersMaxNo,
          })}
        </div>
        <div>
          <Input
            label={translate(resources.accounts.dialog.email)}
            value={form.email}
            onChange={e =>
              setForm(
                produce(d => {
                  d.email = e.value;
                }),
              )
            }
            errors={errors.email}
            clearErrors={() => clearErrors('email')}
            mandatory
          />
          <Dropdown
            label={translate(resources.accounts.dialog.role.label)}
            value={
              form.role ?? { text: translate(resources.select), value: null }
            }
            onChange={e =>
              setForm(
                produce(d => {
                  d.role = e.value;
                }),
              )
            }
            data={roles ?? []}
            textField="text"
            dataItemKey="value"
            errors={errors.role}
            clearErrors={() => clearErrors('role')}
            helper={translate(resources.accounts.dialog.role.info)}
            mandatory
          />
          <Input
            label={getOptionalLabel(
              translate(resources.accounts.dialog.firstName),
              translate,
            )}
            value={form.firstname}
            onChange={e =>
              setForm(
                produce(d => {
                  d.firstname = e.value;
                }),
              )
            }
            errors={errors.firstname}
            clearErrors={() => clearErrors('firstname')}
          />
          <Input
            label={getOptionalLabel(
              translate(resources.accounts.dialog.lastName),
              translate,
            )}
            value={form.lastname}
            onChange={e =>
              setForm(
                produce(d => {
                  d.lastname = e.value;
                }),
              )
            }
            errors={errors.lastname}
            clearErrors={() => clearErrors('lastname')}
          />
          <Input
            label={getOptionalLabel(
              translate(resources.accounts.dialog.phoneNumber),
              translate,
            )}
            value={form.phoneNumber}
            onChange={e =>
              setForm(
                produce(d => {
                  d.phoneNumber = e.value;
                }),
              )
            }
            errors={errors.phoneNumber}
            clearErrors={() => clearErrors('phoneNumber')}
            controlClassName="mb-0"
          />
        </div>
      </div>
      {!!errors.null?.length && (
        <Notification
          type={NotificationType.Error}
          scrollUp={false}
          messages={errors.null.map(err => ({
            message: getErrorMessage(err, translate),
          }))}
        />
      )}
      <div className="dialog-actions vertical">
        <Button
          className="btn btn-solid"
          onClick={onSaveClick}
          loading={isSaveLoading}
        >
          <span className="btn-text">
            {translate(resources.accounts.dialog.add.save)}
          </span>
        </Button>
      </div>
    </Dialog>
  );
};
